import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Text for a live region that announces even when it repeats.
 *
 * **The defect this exists for.** A live region announces a CHANGE. Setting the
 * same string twice changes nothing, so React writes nothing to the DOM and a
 * screen reader says nothing — and the repeat is exactly the case that matters:
 * the resend that exists because the first message never arrived, the second
 * authenticator app, the second factor removed. Confirmed to a sighted user by
 * something appearing, and to everyone else by silence.
 *
 * Blanking for a beat before re-setting makes the change real. That is all the
 * state there is; the text itself stays derived from whatever the caller sends.
 *
 * **Third time written, first time shared.** `MfaChallenge` grew it, then the
 * enrolment screens needed it and did not have it. Two review rounds were spent
 * finding the same defect in each new copy, which is the signal that it is not
 * per-component knowledge.
 *
 * The region using this must be mounted from the first render and must not be
 * hidden with `display: none` while empty — a hidden live region is not in the
 * accessibility tree, so filling it is an insertion, which is the shape
 * assistive tech handles worst. `min-height` instead.
 */
export function useAnnouncement(): [string, (text: string) => void] {
  const [text, setText] = useState('')
  const [blank, setBlank] = useState(false)
  const timer = useRef<number | undefined>(undefined)

  // Nothing may set state after unmount — the blank is on a timer.
  useEffect(() => () => window.clearTimeout(timer.current), [])

  /**
   * Stable for the lifetime of the component — issue 218.
   *
   * It was a plain function declaration, so every render produced a new
   * identity, and a caller that put `announce` in a dependency array got a list
   * that could never match. `AddTotp` had the other half of the same problem: it
   * omitted `announce` instead, which is what `exhaustive-deps` reported the day
   * the react plugin was switched on.
   *
   * The empty dep list is honest rather than convenient. This closes over
   * `setText`, `setBlank` and `timer` and nothing else, and React guarantees the
   * identity of all three, so there is no value here that could go stale.
   */
  const announce = useCallback((next: string) => {
    window.clearTimeout(timer.current)
    setText(next)
    // Only worth a blank when it would otherwise be silent. An empty region
    // becoming non-empty is already a change; blanking that would announce the
    // same thing a beat later for no reason.
    setBlank(true)
    timer.current = window.setTimeout(() => setBlank(false), 120)
  }, [])

  return [blank ? '' : text, announce]
}
